import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import ErrorMessage from "../Components/ErrorMessage";
import { useWithLang } from "../i18n";
import CheckMarkIcon from "../icons/CheckMarkIcon";
import CloseIcon from "../icons/CloseIcon";
import Api, { parseErrorValidation } from "../utils/api";
import Button from "./Button";
import FormLoading from "./FormLoading";
import InputField from "./InputField";
import Modal, { useModal } from "./Modal";

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const sponsorsInputValidation = Yup.object().shape({
  fullname: Yup.string().required("Full Name is a required field").min(3),
  email: Yup.string()
    .email("Format email not valid")
    .required("Email is a required field")
    .min(3),
  phone: Yup.string().matches(phoneRegExp, "Phone Number is not valid"),
  company_name: Yup.string()
    .required("Company Name is a required field")
    .min(3),
  sponsor_type: Yup.string().required("Sponsor Type is a required field"),
});

const tierSelect = [
  {
    name: "Bronze",
    type: 4,
  },
  {
    name: "Silver",
    type: 3,
  },
  {
    name: "Gold",
    type: 2,
  },
  {
    name: "Platinum",
    type: 1,
  },
];

const tiers = [
  {
    name: "Bronze",
    id: "tier-bronze",
    href: "#",
    priceMonthly: "IDR 75.000.000",
    description:
      "The perfect plan if you're just getting started with our product.",
    features: [
      {
        name: "Stage Screen Benefit",
        featuresSub: ["Company Logo in E-Flyer"],
      },
      {
        name: "Benefits Sponsor",
        featuresSub: [
          "Company Logo on Social Media Content",
          "Company Logo in Event Report",
        ],
      },
    ],
    featured: false,
    type: 4,
  },
  {
    name: "Platinum",
    id: "tier-platinum",
    href: "#",
    priceMonthly: "IDR 250.000.000",
    description: "Dedicated support and infrastructure for your company.",
    features: [
      {
        name: "Presentation",
        featuresSub: ["1x Presentation Session (Max. 15 Minutes)"],
      },
      {
        name: "Stage Screen Benefit",
        featuresSub: [
          "Company Logo in E-Flyer",
          "TVC Looping Before Speaking Session and During Break Session",
        ],
      },
      {
        name: "Booth",
        featuresSub: ["Booth Space (2 Tables & 4 Chairs)", "2 amp Electricity"],
      },
      {
        name: "Benefits Sponsor",
        featuresSub: [
          "Company Logo on Social Media Content",
          "Company Name in Welcome Gate",
          "Company Logo in Event Report",
          "Company Logo in Goodie Bag",
          "Company Logo in Name Tag",
          "Entitled to Give Merchandise",
        ],
      },
    ],
    featured: true,
    type: 1,
  },
  {
    name: "Gold",
    id: "tier-gold",
    href: "#",
    priceMonthly: "IDR 150.000.000",
    description: "Dedicated support and infrastructure for your company.",
    features: [
      {
        name: "Presentation",
        featuresSub: ["1x Presentation Session (Max. 10 Minutes)"],
      },
      {
        name: "Stage Screen Benefit",
        featuresSub: [
          "Company Logo in E-Flyer",
          "TVC Looping Before Speaking Session and During Break Session",
        ],
      },
      {
        name: "Booth",
        featuresSub: ["Booth Space (1 Table & 2 Chairs)", "2 amp Electricity"],
      },
      {
        name: "Benefits Sponsor",
        featuresSub: [
          "Company Logo on Social Media Content",
          "Company Name in Welcome Gate",
          "Company Logo in Event Report",
          "Company Logo in Name Tag",
          "Entitled to Give Merchandise",
        ],
      },
    ],
    featured: true,
    type: 2,
  },
  {
    name: "Silver",
    id: "tier-silver",
    href: "#",
    priceMonthly: "IDR 100.000.000",
    description:
      "The perfect plan if you're just getting started with our product.",
    features: [
      {
        name: "Stage Screen Benefit",
        featuresSub: ["Company Logo in E-Flyer"],
      },
      {
        name: "Booth",
        featuresSub: ["Booth Space (1 Table & 2 Chairs)", "2 amp Electricity"],
      },
      {
        name: "Benefits Sponsor",
        featuresSub: [
          "Company Logo on Social Media Content",
          "Company Name in Welcome Gate",
          "Company Logo in Event Report",
        ],
      },
    ],
    featured: false,
    type: 3,
  },
];

function BannerSponsorship() {
  const { t } = useTranslation();
  const { f } = useWithLang();
  const [typeSponsor, setTypeSponsor] = useState(0);

  const sponsorsInputInitialValues = {
    fullname: "",
    email: "",
    phone: "",
    company_name: "",
    sponsor_type: typeSponsor === 0 ? "" : typeSponsor,
  };

  const handleData = (data: any) => {
    setTypeSponsor(data);
  };

  const modalSponsors = useModal();

  const modalClick = () => {
    modalSponsors.setIsOpen(true);
  };

  const onSubmitInputSponsor = async (values: any, { setErrors }) => {
    console.log(values);

    try {
      await Api.post("/public/sponsorship", values);
      toast.success(t("Your Request Send sucessfully!"));
      modalSponsors.setIsOpen(false);
    } catch (e) {
      if (e.response?.data?.errors) {
        setErrors(parseErrorValidation(e.response?.data?.errors));
      } else {
        toast.error(
          e.response?.data?.message || "Something went wrong!, please try again"
        );
      }
    }
  };
  return (
    <>
      <div className="bg-pandi p-14 text-white justify-center justify-items-center space-y-5 mb-10 text-center">
        <div className="text-4xl">{t("text-sponsor-1")}</div>
        <div>{t("text-sponsor-2")}</div>
        <div>
          <Button
            className="mb-4 md:text-base font-sans"
            color="primary"
            onClick={modalClick}
          >
            {t("button-sponsor")}
          </Button>
          <Modal
            {...modalSponsors}
            className="bg-white p-4 rounded-lg max-w-screen-lg"
          >
            <div className="flex justify-between py-5">
              <div className="">
                <div className="font-bold text-xl">
                  {t("Sponsorship Interest Form")}
                </div>
                <div>
                  {t("Request our sponsorship & speaking opportunities")}
                </div>
              </div>
              <button
                className="hover:opacity-50 p-1"
                onClick={() => modalSponsors.setIsOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div>
              <Formik
                initialValues={sponsorsInputInitialValues}
                onSubmit={onSubmitInputSponsor}
                validationSchema={sponsorsInputValidation}
              >
                {({ isSubmitting, values, handleChange }) => (
                  <Form>
                    <FormLoading isLoading={isSubmitting}>
                      <div className="flex flex-wrap -mx-4">
                        <div className="p-4 w-full">
                          <label className="block mb-2 font-bold">
                            {t("Full Name")}
                          </label>
                          <InputField
                            className="form-input w-full"
                            name="fullname"
                            type="text"
                            placeholder={t("Full Name")}
                          />
                          <ErrorMessage name="fullname" />
                        </div>
                        <div className="p-4 w-full">
                          <label className="block mb-2 font-bold">
                            {t("Email")}
                          </label>
                          <InputField
                            className="form-input w-full"
                            name="email"
                            placeholder={t("Email")}
                          />
                          <ErrorMessage name="email" />
                        </div>
                        <div className="p-4 w-full">
                          <label className="block mb-2 font-bold">
                            {t("Company Name")}
                          </label>
                          <InputField
                            className="form-input w-full"
                            name="company_name"
                            placeholder={t("Company Name")}
                          />
                          <ErrorMessage name="company_name" />
                        </div>
                        <div className="p-4 w-full">
                          <label className="block mb-2 font-bold">
                            {t("Phone Number")}
                          </label>
                          <InputField
                            className="form-input w-full"
                            name="phone"
                            type="text"
                            placeholder={t("Phone Number")}
                          />
                          <ErrorMessage name="phone" />
                        </div>
                        <div className="p-4 w-full">
                          <label className="block mb-2 font-bold">
                            {t("Sponsor Type")}
                          </label>
                          <Field
                            as="select"
                            className="form-input w-full px-3 py-2 rounded-md border-gray-300"
                            name="sponsor_type"
                            value={values.sponsor_type}
                            onChange={handleChange}
                          >
                            <option label={t("Select Type")}>""</option>
                            {tierSelect.map((tier, id) => (
                              <option key={id} value={tier.type}>
                                {tier.name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="sponsor_type" />
                        </div>
                        <h3 className="p-4 w-full">
                          {t(
                            "Enter as many details as possible about your vision and will make it happen!"
                          )}
                        </h3>
                        <div className="p-4 w-full flex justify-end gap-2">
                          <button
                            className="bg-gray-300 hover:opacity-80 px-3 rounded-md"
                            type="button"
                            onClick={() => {
                              modalSponsors.setIsOpen(false);
                            }}
                          >
                            Cancel
                          </button>
                          <Button
                            type="submit"
                            color="primary"
                            className="font-bold"
                          >
                            {t("Send")}
                          </Button>
                        </div>
                      </div>
                    </FormLoading>
                  </Form>
                )}
              </Formik>
            </div>
          </Modal>
        </div>
      </div>
      <Subscribers typeData={handleData} onOpen={modalClick} />
    </>
  );
}

export default BannerSponsorship;

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Subscribers({ typeData, onOpen }) {
  const modalSponsors = useModal();

  const modalClick = () => {
    modalSponsors.setIsOpen(true);
  };

  return (
    <div className="bg-white px-6 py-14 lg:px-8">
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-base font-semibold text-red-600">Jenis Sponsor</h2>
        <p className="mt-2 text-balance text-2xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
          Pilih jenis sponsor yang tepat untuk Anda
        </p>
      </div>
      <div className="mx-auto mt-14 grid max-w-2xl grid-cols-1 items-center sm:items-end gap-y-6 sm:mt-16 sm:gap-y-0 lg:max-w-7xl lg:grid-cols-4">
        {tiers.map((tier, tierIdx) => (
          <div
            key={tier.id}
            className={classNames(
              tier.featured ? "relative bg-white" : "bg-white sm:mx-8 lg:mx-0",
              tier.featured
                ? ""
                : tierIdx === 0
                ? "rounded-t-3xl sm:rounded-b-none lg:rounded-bl-3xl lg:rounded-tr-none sm:h-3/4"
                : "sm:rounded-t-none lg:rounded-bl-none lg:rounded-br-3xl lg:rounded-tr-3xl sm:h-5/6",
              "rounded-3xl sm:rounded-b-none p-4 ring-1 ring-gray-900/10 sm:p-6 flex flex-col justify-between"
            )}
          >
            <div>
              <h3
                id={tier.id}
                className={classNames(
                  "text-red-600 text-3xl",
                  "text-base/7 font-semibold"
                )}
              >
                {tier.name}
              </h3>
              <p className="mt-4 flex items-baseline gap-x-2">
                <span
                  className={classNames(
                    "text-gray-900",
                    "text-2xl font-semibold tracking-tight"
                  )}
                >
                  {tier.priceMonthly}
                </span>
                {/* <span className={classNames("text-gray-500", "text-base")}>
                /month
              </span> */}
              </p>
              {/* <p className={classNames("text-gray-600", "mt-6 text-base/7")}>
              {tier.description}
            </p> */}
              <ul
                role="list"
                className={classNames(
                  "text-gray-600",
                  "mt-3 space-y-3 text-sm/6 sm:mt-5"
                )}
              >
                {tier.features.map((feature: any) => (
                  <>
                    <li key={feature} className="flex gap-x-2 underline">
                      {feature.name}
                    </li>
                    {feature.featuresSub.map((feat: any, i: number) => (
                      <div key={i} className="flex gap-x-2 text-sm">
                        <CheckMarkIcon
                          aria-hidden="true"
                          className={classNames(
                            tier.featured ? "text-red-400" : "text-red-600",
                            "h-6 w-4 flex-none"
                          )}
                        />
                        {feat}
                      </div>
                    ))}
                  </>
                ))}
              </ul>
            </div>
            <div
              aria-describedby={tier.id}
              className={classNames(
                "bg-red-500 text-white shadow-sm hover:bg-red-400 focus-visible:outline-red-500",
                "mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10 cursor-pointer"
              )}
              onClick={() => {
                onOpen();
                typeData(tier.type);
              }}
            >
              Pilih
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
